/** Live IndexedDB mirror. Browser storage remains the source of truth. */
const TAB_KEY = '__menucheff_indexeddb_tab__';
const INSTALL_KEY = '__menucheffStorageMirror';

function parseValue(key, rawValue) {
  try {
    const json = /(?:-User|@User)$/.test(key) ? atob(rawValue) : rawValue;
    return JSON.parse(json);
  } catch (_) {
    return rawValue;
  }
}

export function startStorageMirror() {
  if (typeof window === 'undefined' || !window.indexedDB) return;
  if (window[INSTALL_KEY]) return;
  const state = { database: null, pending: [], retryAt: 0 };
  window[INSTALL_KEY] = state;
  const originals = {};
  const fingerprints = new Map();
  ['setItem', 'removeItem', 'clear'].forEach((name) => {
    originals[name] = Storage.prototype[name];
  });
  let tabId;
  try {
    tabId = sessionStorage.getItem(TAB_KEY) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    originals.setItem.call(sessionStorage, TAB_KEY, tabId);
  } catch (_) {
    tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function snapshot(source) {
    const scope = source === 'sessionStorage' ? tabId : 'origin';
    const records = [];
    if (source === 'cookies') {
      document.cookie.split(';').forEach((cookie) => {
        const separator = cookie.indexOf('=');
        if (separator < 0) return;
        const key = cookie.slice(0, separator).trim();
        const rawValue = cookie.slice(separator + 1).trim();
        records.push({ id: JSON.stringify([source, scope, key]), source, scope,
          key, rawValue, value: parseValue(key, rawValue) });
      });
    } else {
      const storage = window[source];
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key === TAB_KEY) continue;
        const rawValue = storage.getItem(key);
        records.push({ id: JSON.stringify([source, scope, key]), source, scope,
          key, rawValue, value: parseValue(key, rawValue) });
      }
    }
    return { source, scope, records };
  }

  function persist(batch) {
    if (!state.database) {
      // Keep only the latest real snapshot per source while the database opens.
      state.pending = state.pending.filter((item) => item.source !== batch.source);
      state.pending.push(batch);
      return;
    }
    try {
      const transaction = state.database.transaction('entries', 'readwrite');
      const store = transaction.objectStore('entries');
      const request = store.index('sourceScope').openCursor(
        IDBKeyRange.only([batch.source, batch.scope]),
      );
      const remaining = new Map(batch.records.map((record) => [record.id, record]));
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const record = remaining.get(cursor.primaryKey);
          if (!record) cursor.delete();
          else {
            if (cursor.value.rawValue !== record.rawValue) {
              cursor.update({ ...record, updatedAt: new Date().toISOString() });
            }
            remaining.delete(cursor.primaryKey);
          }
          cursor.continue();
        } else {
          remaining.forEach((record) => {
            store.put({ ...record, updatedAt: new Date().toISOString() });
          });
        }
      };
      transaction.onabort = () => {
        fingerprints.delete(batch.source);
        console.error('Falha ao sincronizar IndexedDB:', transaction.error);
      };
    } catch (error) {
      fingerprints.delete(batch.source);
      console.error('Falha ao sincronizar IndexedDB:', error);
    }
  }

  function sync(source) {
    try {
      const batch = snapshot(source);
      const fingerprint = JSON.stringify(batch.records);
      if (fingerprints.get(source) === fingerprint) return;
      fingerprints.set(source, fingerprint);
      persist(batch);
    } catch (error) {
      console.error('Falha ao ler armazenamento da sessão:', error);
    }
  }

  function openDatabase() {
    if (state.database || Date.now() < state.retryAt) return;
    state.retryAt = Date.now() + 10000;
    let request;
    try { request = indexedDB.open('menucheff-session', 1); } catch (error) {
      console.error('IndexedDB indisponível:', error);
      return;
    }
    request.onupgradeneeded = () => {
      const store = request.result.createObjectStore('entries', { keyPath: 'id' });
      store.createIndex('sourceScope', ['source', 'scope']);
      store.createIndex('source', 'source');
      store.createIndex('key', 'key');
    };
    request.onsuccess = () => {
      state.database = request.result;
      state.database.onversionchange = () => {
        state.database.close();
        state.database = null;
      };
      state.database.onclose = () => { state.database = null; };
      const pending = state.pending;
      state.pending = [];
      pending.forEach(persist);
      fingerprints.clear();
      ['localStorage', 'sessionStorage', 'cookies'].forEach(sync);
    };
    request.onerror = () => console.error('IndexedDB indisponível:', request.error);
    request.onblocked = () => console.warn('IndexedDB aguardando fechamento de outra conexão.');
  }

  ['setItem', 'removeItem', 'clear'].forEach((name) => {
    Storage.prototype[name] = function mirroredStorageMutation(...args) {
      const result = originals[name].apply(this, args);
      if (this === window.sessionStorage && name === 'clear') {
        originals.setItem.call(this, TAB_KEY, tabId);
      }
      if (this === window.localStorage) {
        sync('localStorage');
        window.dispatchEvent(new Event('menucheff:storage-changed'));
      }
      else if (this === window.sessionStorage) sync('sessionStorage');
      return result;
    };
  });
  window.addEventListener('storage', (event) => {
    if (event.storageArea === window.localStorage) sync('localStorage');
    else if (event.storageArea === window.sessionStorage) sync('sessionStorage');
  });
  // Cookie Store events are immediate where supported. Polling also catches
  // HTTP Set-Cookie, expiration, and legacy property assignments.
  let previousCookies = document.cookie;
  if (window.cookieStore) {
    window.cookieStore.addEventListener('change', () => sync('cookies'));
  }
  window.setInterval(() => {
    openDatabase();
    sync('localStorage');
    sync('sessionStorage');
    const cookies = document.cookie;
    if (cookies !== previousCookies) {
      previousCookies = cookies;
      sync('cookies');
    }
  }, 1000);
  const reconcile = () => ['localStorage', 'sessionStorage', 'cookies'].forEach(sync);
  window.addEventListener('focus', reconcile);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) reconcile();
  });
  reconcile();
  openDatabase();
}

startStorageMirror();
